import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

/**
 * Modern Dashboard Component
 * Ultra-modern design with glassmorphism, animations, and excellent UX
 */
const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalTasks: 0,
    completed: 0,
    inProgress: 0,
    pendingOnboarding: 0,
  });
  const [loading, setLoading] = useState(true);
  const [greeting, setGreeting] = useState('');

  const isAdmin = user.role === 'Admin';
  const isManager = user.role === 'Manager' || isAdmin;

  // Set greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/companies');
      const companiesData = res.data.data || res.data;
      
      let totalTasks = 0, completed = 0, inProgress = 0, pendingOnboarding = 0;
      const activities = [];

      for (const company of companiesData) {
        const companyTasks = company.CompanyTasks || [];
        totalTasks += companyTasks.length;
        
        companyTasks.forEach(t => {
          if (t.status === 'Completed') completed++;
          else inProgress++;
          
          // Add to recent activity with descriptive task names
          activities.push({
            id: t.id,
            type: 'task',
            title: t.type || 'Task Update', // Use task type for descriptive names
            company: company.name,
            status: t.status,
            time: t.updatedAt || t.createdAt,
          });
        });

        // Count branches needing onboarding
        if (company.Branches) {
          pendingOnboarding += company.Branches.filter(b => !b.onboardingComplete).length;
        }
      }

      // Sort activities by time (most recent first) and take top 5
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));

      setCompanies(companiesData);
      setStats({
        totalCompanies: companiesData.length,
        totalTasks,
        completed,
        inProgress,
        pendingOnboarding,
      });
      setRecentActivity(activities.slice(0, 5));
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const completionRate = stats.totalTasks > 0 
    ? Math.round((stats.completed / stats.totalTasks) * 100) 
    : 0;

  return (
    <div className="relative z-10 pb-safe">
      <div className="py-2">
        {/* Header - Mobile Optimized */}
        <header className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className="text-gray-500 dark:text-slate-400 text-xs sm:text-sm font-medium tracking-wide uppercase mb-1">
                {greeting}
              </p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                {user.name || 'User'}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="px-2 sm:px-3 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full text-xs sm:text-sm font-medium border border-blue-200 dark:border-blue-500/30">
                  {user.role}
                </span>
                <span className="text-gray-500 dark:text-slate-500 text-xs sm:text-sm">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/companies')}
                className="px-4 py-2.5 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 border border-gray-300 dark:border-white/10 rounded-xl text-gray-700 dark:text-white font-medium transition-all duration-200 backdrop-blur-sm text-sm sm:text-base"
              >
                View Companies
              </button>
              {isManager && (
                <button
                  onClick={() => navigate('/companies')}
                  className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-xl text-white font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 text-sm sm:text-base"
                >
                  + New Company
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Companies"
            value={stats.totalCompanies}
            icon="🏢"
            color="blue"
            onClick={() => navigate('/companies')}
          />
          <StatCard
            title="Active Tasks"
            value={stats.inProgress}
            icon="⚡"
            color="amber"
          />
          <StatCard
            title="Completed"
            value={stats.completed}
            icon="✓"
            color="emerald"
          />
          <StatCard
            title="Pending Onboarding"
            value={stats.pendingOnboarding}
            icon="📋"
            color="violet"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Progress Overview - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Ring Card */}
            <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Onboarding Progress</h2>
              
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* Progress Ring */}
                <div className="relative">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-200 dark:text-slate-700"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="url(#gradient)"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${completionRate * 4.4} 440`}
                      className="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#10B981" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">{completionRate}%</span>
                    <span className="text-gray-600 dark:text-slate-400 text-sm">Complete</span>
                  </div>
                </div>

                {/* Progress Breakdown */}
                <div className="flex-1 space-y-4 w-full">
                  <ProgressBar 
                    label="Completed Tasks" 
                    value={stats.completed} 
                    max={stats.totalTasks || 1} 
                    color="emerald" 
                  />
                  <ProgressBar 
                    label="In Progress" 
                    value={stats.inProgress} 
                    max={stats.totalTasks || 1} 
                    color="blue" 
                  />
                  <ProgressBar 
                    label="Companies Onboarded" 
                    value={companies.filter(c => c.Branches?.some(b => b.onboardingComplete)).length} 
                    max={stats.totalCompanies || 1} 
                    color="violet" 
                  />
                </div>
              </div>
            </div>

            {/* Company Cards */}
            <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Companies</h2>
                <button
                  onClick={() => navigate('/companies')}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-sm font-medium transition-colors"
                >
                  View All →
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {companies.slice(0, 4).map((company) => (
                  <CompanyCard key={company.id} company={company} onClick={() => navigate(`/company/${company.id}`)} />
                ))}
              </div>
              
              {companies.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">🏢</div>
                  <p className="text-gray-600 dark:text-slate-400">No companies yet</p>
                  {isManager && (
                    <button
                      onClick={() => navigate('/companies')}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      Create First Company
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <QuickActionButton
                  icon="🏢"
                  label="Manage Companies"
                  onClick={() => navigate('/companies')}
                />
                {isAdmin && (
                  <QuickActionButton
                    icon="👥"
                    label="Manage Users"
                    onClick={() => navigate('/users')}
                  />
                )}
                <QuickActionButton
                  icon="📊"
                  label="View Reports"
                  onClick={() => navigate('/reports')}
                />
                {isAdmin && (
                  <>
                    <QuickActionButton
                      icon="⚙️"
                      label="Services"
                      onClick={() => navigate('/admin/services')}
                    />
                    <QuickActionButton
                      icon="📜"
                      label="Audit Log"
                      onClick={() => navigate('/admin/audit-log')}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h2>
              <div className="space-y-3">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity, idx) => (
                    <ActivityItem key={idx} activity={activity} />
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-slate-500 text-sm text-center py-4">No recent activity</p>
                )}
              </div>
            </div>

            {/* System Status */}
            <div className="bg-emerald-50 dark:bg-gradient-to-br dark:from-emerald-500/20 dark:to-emerald-600/10 backdrop-blur-xl rounded-2xl border border-emerald-200 dark:border-emerald-500/20 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">System Online</span>
              </div>
              <p className="text-gray-600 dark:text-slate-400 text-sm">
                All services operational. Last sync: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, onClick }) => {
  const iconColors = {
    blue: 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    violet: 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
  };

  return (
    <div
      onClick={onClick}
      className="bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 rounded-xl p-4 cursor-pointer transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg ${iconColors[color]}`}>
          {icon}
        </div>
        {onClick && (
          <span className="text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-400 transition-colors">→</span>
        )}
      </div>
      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</div>
      <div className="text-gray-600 dark:text-slate-400 text-sm">{title}</div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, max, color }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const colors = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    violet: 'bg-violet-500',
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-600 dark:text-slate-400">{label}</span>
        <span className="text-gray-900 dark:text-white font-medium">{value}/{max}</span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${colors[color]} rounded-full transition-all duration-1000 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Company Card Component
const CompanyCard = ({ company, onClick }) => {
  const branchCount = company.Branches?.length || 0;
  const taskCount = company.CompanyTasks?.length || 0;

  return (
    <div
      onClick={onClick}
      className="bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 rounded-xl p-4 cursor-pointer transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg flex items-center justify-center text-white font-bold">
          {company.name?.charAt(0) || '?'}
        </div>
        <span className="text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-400 transition-colors">→</span>
      </div>
      <h3 className="text-gray-900 dark:text-white font-semibold mb-1 truncate">{company.name}</h3>
      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-slate-400">
        <span>{branchCount} branches</span>
        <span>{taskCount} tasks</span>
      </div>
    </div>
  );
};

// Quick Action Button Component
const QuickActionButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 rounded-xl text-left transition-all duration-200 group"
  >
    <span className="text-xl">{icon}</span>
    <span className="text-gray-900 dark:text-white font-medium flex-1">{label}</span>
    <span className="text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-400 transition-colors">→</span>
  </button>
);

// Activity Item Component
const ActivityItem = ({ activity }) => {
  const statusColors = {
    Completed: 'bg-emerald-500',
    'In Progress': 'bg-blue-500',
    Pending: 'bg-amber-500',
  };

  return (
    <div className="flex items-start gap-3">
      <div className={`w-2 h-2 rounded-full mt-2 ${statusColors[activity.status] || 'bg-gray-400 dark:bg-slate-500'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-gray-900 dark:text-white text-sm truncate">{activity.title}</p>
        <p className="text-gray-500 dark:text-slate-500 text-xs truncate">{activity.company}</p>
      </div>
      <span className="text-gray-500 dark:text-slate-500 text-xs whitespace-nowrap">
        {activity.time ? new Date(activity.time).toLocaleDateString() : 'Recently'}
      </span>
    </div>
  );
};

export default Dashboard;
